<div class="vstack h-100">
    <div class="offcanvas-header px-4">
        <h5 class="offcanvas-title fw-semibold mb-0">
            {{ booking.docket ? (editMode === 'limits' ? 'Edit course limits' : 'Edit choices') : 'Add Pre-Order' }}
        </h5>
        <button type="button" class="btn-close" aria-label="Close" (click)="cancel()"></button>
    </div>
    <div class="offcanvas-body p-0 overflow-auto">
        <div class="p-4 vstack gap-3">
            <ng-container *ngIf="loading; else loaded">
                <div class="text-body-secondary small">Loading menus...</div>
            </ng-container>
            <ng-template #loaded>
                <ng-container *ngIf="!loadError; else loadErrorTpl">
                    @if (!booking.docket) {
                        <form (ngSubmit)="submit()" [formGroup]="form" class="vstack gap-1">
                            <div class="vstack gap-2">
                                <label class="form-label fw-semibold mb-1">
                                    Menu to collect choices from
                                </label>
                                <select 
                                    class="form-select" 
                                    data-testid="select-menu"
                                    formControlName="menuId" 
                                    [disabled]="menus.length === 0"
                                >
                                    <option value="" disabled selected>
                                        Select a menu
                                    </option>
                                    <option *ngFor="let menu of menus" [value]="menu.id">
                                        {{ menu.displayName }}
                                    </option>
                                </select>
                                <div class="form-text" *ngIf="menus.length === 0">
                                    No menus available in this venue's product library.
                                </div>
                            </div>
                            <ng-container *ngTemplateOutlet="courseSettings"></ng-container>
                            <hr />
                            <div class="vstack gap-2">
                                <label class="form-label fw-semibold mb-1">
                                    Order Settings
                                </label>
                                <div class="hstack align-items-center justify-content-between gap-3">
                                <label class="form-check-label" for="perCoverChoicesSwitch">
                                    Collect choices per person
                                </label>
                                  <div class="form-check form-switch">
                                      <input
                                          class="form-check-input"
                                          type="checkbox"
                                          id="perCoverChoicesSwitch"
                                          [checked]="true"
                                          disabled
                                      />
                                  </div>
                                </div>
                                <div class="form-text">
                                    The customer will be asked to select menu choices for each individual guest in their booking.
                                    <br/>
                                    Non-personalised orders (one set of choices for the whole booking) will be available in a future update.
                                </div>
                            </div>
                        </form>
                    } @else if (menu) {
                        @if (editMode === 'limits') {
                            <div class="fw-semibold">{{ menu.displayName }}</div>
                            <ng-container *ngTemplateOutlet="courseSettings"></ng-container>
                        } @else if (editingCoverIndex === null) {
                            <div class="vstack gap-3">
                                <div class="vstack gap-1">
                                    <div class="fw-semibold">{{ menu.displayName }}</div>
                                    <div class="text-body-secondary small">
                                        {{ draftCoverChoices.length }}/{{ booking.size }} guest entries · {{ completedCoverCount }} with choices
                                    </div>
                                </div>
                                @if (missingCoverCount > 0) {
                                    <div class="rounded border bg-body-tertiary p-3 hstack gap-2 justify-content-between align-items-center flex-wrap">
                                        <div class="small text-body-secondary">
                                            {{ missingCoverCount }} guest {{ missingCoverCount === 1 ? 'entry is' : 'entries are' }} missing.
                                        </div>
                                        <div class="hstack gap-2">
                                            <button
                                                type="button"
                                                class="btn btn-sm btn-outline-secondary hstack gap-2 text-nowrap"
                                                data-testid="add-empty-docket-entry-button"
                                                (click)="addEmptyCoverEntry()"
                                            >
                                                Add Empty Guest
                                                <img ngSrc="assets/plus/normal.svg" alt="Add" height="14" width="14" />
                                            </button>
                                            <button
                                                type="button"
                                                class="btn btn-sm btn-outline-secondary text-nowrap"
                                                data-testid="add-missing-docket-entries-button"
                                                (click)="addMissingCoverEntries()"
                                            >
                                                Add All Missing
                                            </button>
                                        </div>
                                    </div>
                                }
                                <div class="vstack gap-2">
                                    @if (draftCoverChoices.length === 0) {
                                        <div class="rounded border bg-body p-3 text-body-secondary">
                                            No guest entries yet.
                                        </div>
                                    }
                                    @for (choice of draftCoverChoices; track $index) {
                                        <div class="rounded border bg-body vstack" [attr.data-testid]="'editable-docket-choice-' + $index">
                                            <div class="p-3 hstack gap-2 justify-content-between align-items-start flex-wrap">
                                                <div class="vstack gap-1">
                                                    <div class="fw-semibold">{{ getChoiceTitle(choice, $index) }}</div>
                                                    @if (choice.notes) {
                                                        <div class="small text-body-secondary fst-italic">{{ choice.notes }}</div>
                                                    }
                                                </div>
                                                <div class="hstack gap-2 ms-auto">
                                                    <button
                                                        type="button"
                                                        class="btn btn-sm btn-outline-secondary"
                                                        [attr.data-testid]="'edit-docket-choice-' + $index"
                                                        (click)="editCover($index)"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        class="btn btn-sm btn-outline-danger"
                                                        [attr.data-testid]="'delete-docket-choice-' + $index"
                                                        (click)="deleteCover($index)"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                            <hr class="m-0" />
                                            <div class="px-3 py-2 vstack gap-1">
                                                @if (getChoiceSelections(choice).length > 0) {
                                                    @for (selection of getChoiceSelections(choice); track $index) {
                                                        <div>{{ selection }}</div>
                                                    }
                                                } @else {
                                                    <div class="text-body-secondary small">No choices entered.</div>
                                                }
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>
                        } @else if (coverForm) {
                            <form [formGroup]="coverForm" class="vstack gap-3" (ngSubmit)="applyCoverEdit()">
                                <div class="hstack gap-2 justify-content-between align-items-start">
                                    <div class="vstack gap-1">
                                        <h6 class="fw-semibold mb-0">{{ getChoiceTitle(draftCoverChoices[editingCoverIndex], editingCoverIndex) }}</h6>
                                        <div class="text-body-secondary small">{{ selectedCount }} selected</div>
                                    </div>
                                    <button type="button" class="btn btn-sm btn-outline-secondary" (click)="cancelCoverEdit()">
                                        Back
                                    </button>
                                </div>
                                @if (validationErrors.length > 0) {
                                    <div class="alert alert-danger mb-0 vstack gap-1">
                                        @for (error of validationErrors; track $index) {
                                            <div>{{ error }}</div>
                                        }
                                    </div>
                                }
                                <div class="vstack gap-2">
                                    <label class="form-label fw-semibold mb-0" for="coverName">Person Name</label>
                                    <input id="coverName" class="form-control" formControlName="name" placeholder="Enter name" />
                                </div>
                                <div class="vstack gap-2">
                                    <label class="form-label mb-0" for="coverNotes">
                                        <span class="fw-semibold">Special Requirements</span>
                                        <span class="text-body-secondary">(optional)</span>
                                    </label>
                                    <textarea id="coverNotes" class="form-control" rows="3" formControlName="notes" placeholder="Enter special requirements"></textarea>
                                </div>
                                @if (courseLimitsDisplay) {
                                    <div class="alert alert-info mb-0" data-testid="docket-course-limits-summary">
                                        {{ courseLimitsDisplay }}. Choose items only from the courses this guest wants.
                                    </div>
                                }
                                <div class="vstack gap-3" formGroupName="productSelection">
                                    @for (courseGroup of itemsByCourse; track courseGroup.courseId) {
                                        <div class="vstack gap-2" [attr.data-course-id]="courseGroup.courseId">
                                            <div class="hstack justify-content-between align-items-start gap-2">
                                                <div class="vstack gap-0">
                                                    <div class="fw-semibold">{{ courseGroup.courseName }}</div>
                                                    @if (isCourseExcludedFromCount(courseGroup.courseId)) {
                                                        <div class="text-body-secondary small">Extra · does not count towards course limits</div>
                                                    }
                                                    @if (getCourseLimitsDisplay(courseGroup.courseId)) {
                                                        <div class="text-body-secondary small">{{ getCourseLimitsDisplay(courseGroup.courseId) }}</div>
                                                    }
                                                </div>
                                                <div class="text-end small">
                                                    <div class="text-body-secondary">{{ getSelectedCountForCourse(courseGroup.courseId) }} selected</div>
                                                    @if (getCourseValidationError(courseGroup.courseId)) {
                                                        <div class="text-danger">{{ getCourseValidationError(courseGroup.courseId) }}</div>
                                                    }
                                                </div>
                                            </div>
                                            @for (item of courseGroup.items; track item.menuItem.id) {
                                                <label class="btn btn-outline-secondary text-start p-3 vstack gap-2 user-select-none">
                                                    <div class="hstack gap-2 align-items-start">
                                                        <input
                                                            class="form-check-input form-check-input-sm m-0 mt-1"
                                                            type="checkbox"
                                                            [formControlName]="item.menuItem.id"
                                                        />
                                                        <div class="fw-semibold flex-grow-1">{{ item.consumable.displayName || 'Item' }}</div>
                                                    </div>
                                                    @if (item.descriptionFull) {
                                                        <div class="text-body-secondary">
                                                            @if (coverForm.get('ui.expandedDescriptions.' + item.expandedControlName)?.value) {
                                                                <span>{{ item.descriptionFull }}</span>
                                                            } @else {
                                                                <span>{{ item.descriptionShort }}@if (item.canTruncate) { ... }</span>
                                                            }
                                                            @if (item.canTruncate) {
                                                                <button
                                                                    class="btn btn-sm btn-link p-0 ms-1 lh-base"
                                                                    type="button"
                                                                    (click)="toggleDescription(item.expandedControlName)"
                                                                >
                                                                    {{ coverForm.get('ui.expandedDescriptions.' + item.expandedControlName)?.value ? 'Read less' : 'Read more' }}
                                                                </button>
                                                            }
                                                        </div>
                                                    }
                                                    @if (item.modifierGroups.length > 0 && isProductSelected(item.menuItem.id)) {
                                                        @if (modifierSelectionGroup; as modifierSelection) {
                                                            <div [formGroup]="modifierSelection" class="vstack gap-2 mt-2">
                                                                <div class="vstack gap-2" [formGroupName]="item.menuItem.id">
                                                                    @for (group of item.modifierGroups; track group.id) {
                                                                        <div class="vstack gap-1">
                                                                            <div class="vstack gap-0">
                                                                                <div class="fw-semibold text-body-secondary">{{ group.name }}</div>
                                                                                @if (getModifierGroupLimitsDisplay(group)) {
                                                                                    <div class="text-body-secondary small">{{ getModifierGroupLimitsDisplay(group) }}</div>
                                                                                }
                                                                                @if (getModifierGroupValidationError(item.menuItem.id, group)) {
                                                                                    <div class="text-danger small">{{ getModifierGroupValidationError(item.menuItem.id, group) }}</div>
                                                                                }
                                                                            </div>
                                                                            <div class="list-group">
                                                                                @for (modifierAndSkuId of group.modifiers; track modifierAndSkuId.modifier.id) {
                                                                                    @if (modifierAndSkuId.primarySkuId; as modifierSkuId) {
                                                                                        <label class="list-group-item d-flex align-items-center justify-content-between py-2">
                                                                                            <div class="hstack gap-2 align-items-center">
                                                                                                <input
                                                                                                    class="form-check-input form-check-input-sm m-0"
                                                                                                    type="checkbox"
                                                                                                    [formControlName]="modifierSkuId"
                                                                                                />
                                                                                                <span class="fw-semibold">{{ modifierAndSkuId.modifier.displayName }}</span>
                                                                                            </div>
                                                                                            @if (modifierAndSkuId.modifier.description) {
                                                                                                <small class="text-body-secondary ms-2">{{ modifierAndSkuId.modifier.description }}</small>
                                                                                            }
                                                                                        </label>
                                                                                    }
                                                                                }
                                                                            </div>
                                                                        </div>
                                                                    }
                                                                </div>
                                                            </div>
                                                        }
                                                    }
                                                </label>
                                            }
                                        </div>
                                    }
                                </div>
                                <div class="d-flex justify-content-end gap-2">
                                    <button type="button" class="btn btn-outline-secondary" (click)="cancelCoverEdit()">
                                        Cancel
                                    </button>
                                    <button type="submit" class="btn btn-primary" data-testid="apply-docket-choice-button">
                                        Apply
                                    </button>
                                </div>
                            </form>
                        }
                    } @else {
                        <div class="alert alert-danger mb-0">
                            Could not load the menu for this Pre-Order.
                        </div>
                    }
                </ng-container>
            </ng-template>
            <ng-template #courseSettings>
                @if (selectedMenu; as selected) {
                    <fieldset [formGroup]="form" class="rounded border p-3 vstack gap-3 mt-3">
                        <legend class="float-none w-auto px-1 fs-6 fw-semibold mb-0">Courses per guest for this booking</legend>
                        <div class="form-text mt-0">Use the menu default, or choose an override for this booking.</div>
                        <div>
                            <label class="form-label" for="docketMinimumCoursesRequired">Minimum courses per guest</label>
                            <select id="docketMinimumCoursesRequired" class="form-select" formControlName="minimumCoursesRequired" data-testid="docket-minimum-courses-input">
                                <option [ngValue]="null">{{ selected.minimumCoursesRequired ?? 'No minimum' }} (menu default)</option>
                                @for (count of courseCountOptions; track count) {
                                    <option [ngValue]="count">{{ count }}</option>
                                }
                            </select>
                        </div>
                        <div>
                            <label class="form-label" for="docketMaximumCoursesRequired">Maximum courses per guest</label>
                            <select id="docketMaximumCoursesRequired" class="form-select" formControlName="maximumCoursesRequired" data-testid="docket-maximum-courses-input">
                                <option [ngValue]="null">{{ selected.maximumCoursesRequired ?? 'No maximum' }} (menu default)</option>
                                @for (count of courseCountOptions; track count) {
                                    <option [ngValue]="count">{{ count }}</option>
                                }
                            </select>
                        </div>
                        @if ((form.get('minimumCoursesRequired')?.touched && form.get('minimumCoursesRequired')?.invalid) || (form.get('maximumCoursesRequired')?.touched && form.get('maximumCoursesRequired')?.invalid)) {
                            <div class="invalid-feedback d-block">Choose a course count or use the menu default.</div>
                        }
                        @if (form.hasError('minGreaterThanMax')) {
                            <div class="invalid-feedback d-block">Minimum courses cannot exceed maximum courses, including inherited menu defaults.</div>
                        }
                        <div class="small" data-testid="docket-course-limits-summary">
                            {{ courseLimitsDisplay ?? 'Individual course requirements apply' }}
                        </div>
                    </fieldset>
                }
            </ng-template>
            <ng-template #loadErrorTpl>
                <div class="alert alert-danger mb-0">
                    Could not load menus. Please close and try again.
                </div>
            </ng-template>
        </div>
    </div>
    <div class="offcanvas-footer p-3 border-top d-flex justify-content-end gap-2">
        <button class="btn btn-outline-secondary" (click)="cancel()">
            Cancel
        </button>
        <button class="btn btn-primary" (click)="submit()" [disabled]="loading || loadError || form.invalid || (!booking.docket && menus.length === 0) || (booking.docket && !menu)">
            {{ booking.docket ? (editMode === 'limits' ? 'Save course limits' : 'Save choices') : 'Add Pre-Order' }}
        </button>
    </div>
</div>
